import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { actions as warningActions } from 'state/warnings';
import { actions as errorActions } from 'state/errors';

import { actions as orderActions } from 'state/order';
import {
  actions as shipmentsActions,
  selectors as shipmentsSelectors,
} from 'state/shipments';
import { isEmpty } from 'utils/types';
import keyGenerator from 'utils/keys';

import {
  constants as dateAndTimeConstants,
  formatters as dateAndTimeFormatters,
  methods as dateAndTimeMethods,
  validators as dateAndTimeValidators,
} from 'utils/dateAndTimes';

import types from 'state/direct-order-recurring/shipments/actionTypes';
import utils from 'state/direct-order-recurring/shipments/utils';
import {
  handleCleanAddress as handleDirectCleanAddress,
  handleSelectTime as handleDirectSelectTime,
  handleSubmitAddressForm as handleDirectSubmitAddressForm,
  handleTimePickerBlur as handleDirectTimePickerBlur,
} from 'state/direct-order/shipments/effects';

function* handleTimePickers({ payload }) {
  const { stopType, timeParam, value, shipmentKey, diffDay } = payload;

  const key = yield call(utils.getTimeKey, stopType, timeParam);
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);
  const input = yield call(dateAndTimeFormatters.formatRawTime, value);

  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  let computedTimes = { ...originalTimes, [key]: input };
  const datekey = yield call(utils.getDateKey, stopType, timeParam);
  const dateFromkey = yield call(utils.getDateFromKey, stopType);

  const newDateWithDiff = yield call(
    dateAndTimeFormatters.getDateFormatted,
    computedTimes[dateFromkey],
    diffDay
  );

  computedTimes = { ...computedTimes, [datekey]: newDateWithDiff };

  let businessErrors = [];

  const autocompletedInput = yield call(
    dateAndTimeFormatters.autocompleteTimeInputIfCan,
    input
  );
  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    {
      ...computedTimes,
      [key]: autocompletedInput,
    },
    shipmentKey
  );

  if (!!errors && errors[key]) {
    businessErrors = errors[key];
  }

  let isNextDay = false;

  let computedTimesIfInputIsForNextDay = { ...computedTimes };

  if (
    !isEmpty(businessErrors) &&
    diffDay === 0 &&
    timeParam !== dateAndTimeConstants.TIME_FIELD_PARAMS.from
  ) {
    const newDateWithNextDiff = yield call(
      dateAndTimeFormatters.getDateFormatted,
      computedTimes[dateFromkey],
      diffDay + 1
    );
    computedTimesIfInputIsForNextDay = {
      ...computedTimes,
      [datekey]: newDateWithNextDiff,
    };

    const { errors: nextDayErrors } = yield call(
      dateAndTimeValidators.asyncValidateTimes,
      computedTimesIfInputIsForNextDay,
      shipmentKey
    );

    if (!nextDayErrors[key]) isNextDay = true;
  }

  if (!isNextDay) {
    yield put(
      errorActions.setError({
        [fieldKey]: businessErrors,
      })
    );
  }

  const newValues = isNextDay
    ? { ...computedTimesIfInputIsForNextDay }
    : { ...computedTimes };

  yield put(
    shipmentsActions.changeValues({ values: { ...newValues }, shipmentKey })
  );
}

function* setTimeToIfNeeded({ payload }) {
  const { stopType, timeParam, shipmentKey } = payload;
  if (
    stopType !== dateAndTimeConstants.STOP_TYPES.pickup ||
    timeParam !== dateAndTimeConstants.TIME_FIELD_PARAMS.from
  )
    return;
  const originalTimes = yield select(
    shipmentsSelectors.getShipmentTimes,
    shipmentKey
  );
  const pickUpToSetted = yield call(
    dateAndTimeMethods.getAutocompletedPickupToTimeAndDate,
    {
      originalTimes,
    }
  );
  const key = yield call(
    utils.getTimeKey,
    stopType,
    dateAndTimeConstants.TIME_FIELD_PARAMS.to
  );
  const fieldKey = yield call(utils.getTimeKeyFull, shipmentKey, key);

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    {
      ...originalTimes,
      ...pickUpToSetted,
    },
    shipmentKey
  );
  const dropoffTimeToKey = yield call(keyGenerator.shipmentData.time, {
    stopType: 'dropoff',
    timeParam: 'to',
  });

  yield put(warningActions.removeWarning([fieldKey]));
  yield put(
    shipmentsActions.changeValues({
      values: {
        ...pickUpToSetted,
        ...(errors[dropoffTimeToKey] ? { [dropoffTimeToKey]: '' } : {}),
      },
      shipmentKey,
    })
  );

  if (!isEmpty(errors[dropoffTimeToKey])) {
    const fieldWarningKey = yield call(
      utils.getTimeKeyFull,
      shipmentKey,
      dropoffTimeToKey
    );
    yield put(warningActions.setWarning({ [fieldWarningKey]: 'warning' }));
  }
}

function* handleTimePickerBlur(payload) {
  yield call(handleDirectTimePickerBlur, { ...payload, setTimeToIfNeeded });
}

function* handleSelectTime(payload) {
  yield call(handleDirectSelectTime, payload);
}

function* handleDatePickers({ payload }) {
  const { stopType, value, shipmentKey } = payload;
  const key = yield call(utils.getDateFromKey, stopType);

  const times = yield select(shipmentsSelectors.getShipmentTimes, shipmentKey);
  const diff = Math.floor(
    (new Date(value) - new Date(times[key])) / (1000 * 3600 * 24)
  );
  const dateKeys = yield call(utils.getDateKeys, stopType);

  const computedTimesWithDates = yield all(
    Object.keys(times).reduce(
      (acc, currKey) => ({
        ...acc,
        [currKey]:
          dateKeys.indexOf(currKey) >= 0
            ? call(dateAndTimeFormatters.getDateFormatted, times[currKey], diff)
            : times[currKey],
      }),
      {}
    )
  );

  const { errors } = yield call(
    dateAndTimeValidators.asyncValidateTimes,
    computedTimesWithDates,
    shipmentKey
  );

  const timeKeysByStop = yield call(utils.getTimeKeysByStop, stopType);
  const areErrorsFiltered = Object.keys(errors).reduce(
    (acc, keyError) => (timeKeysByStop.indexOf(keyError) >= 0 ? true : acc),
    false
  );

  const priorityRules = yield call(
    dateAndTimeValidators.priorityRules.date,
    stopType
  );

  const cleanedWithPriority = Object.keys(errors).reduce(
    (acc, errorKey) =>
      priorityRules && priorityRules.indexOf(errorKey) >= 0
        ? {
            ...acc,
            [keyGenerator.shipmentData.time({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: '',
            [keyGenerator.shipmentData.date({
              stopType: 'dropoff',
              timeParam: errorKey.split('_')[2],
            })]: computedTimesWithDates.pickup_date_from,
          }
        : { ...acc },

    {}
  );

  if (areErrorsFiltered) {
    const timesCleaned = timeKeysByStop.reduce(
      (acc, curKey) => ({ ...acc, [curKey]: '' }),
      {}
    );

    const newWarnings = timeKeysByStop.reduce(
      (acc, curKey) => ({
        ...acc,
        [utils.getTimeKeyFull(shipmentKey, curKey)]: 'warning',
      }),
      {}
    );

    yield all([
      put(
        shipmentsActions.changeValues({
          values: {
            ...computedTimesWithDates,
            ...cleanedWithPriority,
            ...timesCleaned,
          },
          shipmentKey,
        })
      ),
      put(warningActions.setWarning(newWarnings)),
    ]);
  } else {
    yield put(
      shipmentsActions.changeValues({
        values: { ...computedTimesWithDates, ...cleanedWithPriority },
        shipmentKey,
      })
    );
  }
  yield put(orderActions.fetchQuote());
}

function* handleSubmitAddressForm(payload) {
  yield call(handleDirectSubmitAddressForm, payload);
}

function* handleCleanAddress(payload) {
  yield call(handleDirectCleanAddress, payload);
}

export default function* () {
  // timePickers
  yield takeLatest(types.changeTime$, handleTimePickers);
  yield takeLatest(types.selectTime$, handleSelectTime);
  yield takeLatest(types.onBlurTimePicker$, handleTimePickerBlur);

  // DatePickers
  yield takeLatest(types.changeDate$, handleDatePickers);

  // Addresses
  yield takeLatest(types.changeAddress$, handleSubmitAddressForm);
  yield takeLatest(types.cleanAddress$, handleCleanAddress);
}

export { handleTimePickers, handleTimePickerBlur, handleDatePickers };
